/**
 * One-level comment threads (Google Docs / Word style).
 * Nested replies are flattened onto the root parent.
 */

export type ThreadableComment = {
  id: string;
  createdAt: string;
  parentCommentId?: string | null;
};

export type CommentThread<T extends ThreadableComment = ThreadableComment> = {
  root: T;
  replies: T[];
};

/** If parent is itself a reply, attach to the ultimate root. */
export function flattenReplyParentId(
  parentCommentId: string | null | undefined,
  comments: ReadonlyArray<{ id: string; parentCommentId?: string | null }>
): string | null {
  if (!parentCommentId) return null;
  const byId = new Map(comments.map((c) => [c.id, c]));
  let current: string | null = parentCommentId;
  const seen = new Set<string>();
  while (current) {
    if (seen.has(current)) return parentCommentId;
    seen.add(current);
    const parent = byId.get(current);
    if (!parent) return current;
    if (!parent.parentCommentId) return current;
    current = parent.parentCommentId;
  }
  return parentCommentId;
}

export function isRootComment(comment: { parentCommentId?: string | null }): boolean {
  return comment.parentCommentId == null || comment.parentCommentId === "";
}

export function groupCommentThreads<T extends ThreadableComment>(
  comments: ReadonlyArray<T>
): CommentThread<T>[] {
  const roots = comments.filter((c) => isRootComment(c));
  const repliesByParent = new Map<string, T[]>();

  for (const comment of comments) {
    if (isRootComment(comment)) continue;
    const rootId = flattenReplyParentId(comment.parentCommentId, comments) ?? comment.parentCommentId;
    if (!rootId) continue;
    const list = repliesByParent.get(rootId) ?? [];
    list.push(comment);
    repliesByParent.set(rootId, list);
  }

  const sortByCreated = (a: T, b: T) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

  return roots
    .slice()
    .sort(sortByCreated)
    .map((root) => ({
      root,
      replies: (repliesByParent.get(root.id) ?? []).slice().sort(sortByCreated),
    }));
}

/** Delete root + all replies (local cascade matching DB). */
export function removeCommentThread<T extends { id: string; parentCommentId?: string | null }>(
  comments: ReadonlyArray<T>,
  commentId: string
): T[] {
  const target = comments.find((c) => c.id === commentId);
  if (!target) return [...comments];
  const rootId = isRootComment(target)
    ? target.id
    : flattenReplyParentId(target.parentCommentId, comments) ?? target.parentCommentId ?? target.id;

  return comments.filter((c) => {
    if (c.id === rootId) return false;
    const parentRoot = flattenReplyParentId(c.parentCommentId, comments);
    return parentRoot !== rootId && c.parentCommentId !== rootId;
  });
}

/** When deleting a single reply, leave the rest of the thread. */
export function removeCommentOrThread<T extends { id: string; parentCommentId?: string | null }>(
  comments: ReadonlyArray<T>,
  commentId: string
): T[] {
  const target = comments.find((c) => c.id === commentId);
  if (!target) return [...comments];
  if (isRootComment(target)) return removeCommentThread(comments, commentId);
  return comments.filter((c) => c.id !== commentId);
}
